import {match} from 'ts-pattern';

import {useEffect, useMemo} from 'react';

import type {EagGridStore} from '../store/createEagGridStore';
import type {RowActions, RowData} from '../types/RowData';
import {useLatestRef} from './useLatestRef';

type UseGridSelectionParams = {
  store: EagGridStore;
  rowSelectMode: 'NONE' | 'SINGLE' | 'MULTIPLE';
  selectedRowIds: Set<string>;
  rows: RowData[];
  onRowSelectionChange: ((rows: RowData[]) => void) | undefined;
};

type UseGridSelectionReturn = {
  handleToggleSelection: (rowId: string, extendRange?: boolean) => void;
  handleDeselectAll: () => void;
  /** The `actions` arrays of all currently-selected rows (empty when nothing selected). */
  selectedRowsActions: RowActions[];
};

const getSelectedRows = (rows: RowData[], selectedRowIds: Set<string>): RowData[] =>
  rows.filter((row) => selectedRowIds.has(row.id));

/**
 * Manages row selection logic and fires onRowSelectionChange notifications.
 *
 * Returns:
 * 1. Handlers: handleToggleSelection, handleDeselectAll
 * 2. Selection-change side-effect: fires onRowSelectionChange on all selection change paths
 * 3. Computed selection data: selectedRowsActions — the actions arrays of all currently-selected rows
 *
 * Selection toggle delegates to the correct store method based on rowSelectMode.
 * The onRowSelectionChange effect fires on all selection paths (select-all,
 * deselect-all, row click, bulk action) — not just direct row clicks.
 */
export const useGridSelection = (params: UseGridSelectionParams): UseGridSelectionReturn => {
  const onRowSelectionChangeRef = useLatestRef(params.onRowSelectionChange);

  // Fire onRowSelectionChange with the full selected set whenever selection changes.
  // Using an effect (not inside handleRowClick) ensures it also fires on select-all,
  // deselect-all, and any other selection change paths — not just direct row clicks.
  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: reactive notification; must fire on all selection change paths (select-all, deselect-all, row click, bulk action) — cannot live in a single event handler
  useEffect(
    function notifyRowSelectionChange() {
      if (!onRowSelectionChangeRef.current) {
        return;
      }
      onRowSelectionChangeRef.current(getSelectedRows(params.rows, params.selectedRowIds));
    },
    [params.selectedRowIds, params.rows] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  // Only MULTIPLE mode supports selecting a fixed-anchor checkbox range.
  const handleToggleSelection = (rowId: string, extendRange = false) =>
    match(params.rowSelectMode)
      .with('MULTIPLE', () => {
        if (extendRange) {
          params.store.selectRowRange(rowId);
        } else {
          params.store.toggleRowSelection(rowId);
        }
      })
      .with('SINGLE', () => params.store.setSingleSelection(rowId))
      .with('NONE', () => undefined)
      .exhaustive();

  const handleDeselectAll = () => params.store.deselectAll();

  const selectedRowsActions = useMemo(() => {
    if (params.selectedRowIds.size === 0) {
      return [];
    }
    return getSelectedRows(params.rows, params.selectedRowIds).map((row) => row.actions);
  }, [params.rows, params.selectedRowIds]);

  return {
    handleToggleSelection,
    handleDeselectAll,
    selectedRowsActions,
  };
};
